import { useState } from 'react'
import { listAdminProblemReports } from '../../../api/generated/problem-reports/problem-reports'
import type { ListAdminProblemReportsParams } from '../../../api/generated/model/listAdminProblemReportsParams'
import { Button } from '../../../components/ui/button'
import { DateRangePicker } from '../../../components/ui/date-range-picker'
import { useT } from '../../../i18n'
import { formatDateTime, parsePlainDate, serializePlainDate } from '../../../lib/datetime'
import { useReportRead } from '../own-data'
import { reportCopy } from '../copy'
import { reportInputClass } from '../ReportForm'
import { adminCopy } from './copy'
const statuses = [
  'received',
  'investigating',
  'planned',
  'fixed',
  'closed',
] as const
export default function AdminQueue({
  onSelect,
}: {
  onSelect: (id: string) => void
}) {
  const t = useT(reportCopy),
    a = useT(adminCopy)
  const [draft, setDraft] = useState<ListAdminProblemReportsParams>({})
  const [filters, setFilters] = useState<ListAdminProblemReportsParams>({})
  const [invalid, setInvalid] = useState(false)
  const [cursor, setCursor] = useState<string>()
  const query = useReportRead(['admin', 'queue', filters, cursor], (options) =>
    listAdminProblemReports({ ...filters, cursor, limit: 10 }, options),
  )
  const nextCursor =
    query.data?.status === 200 ? query.data.data.next_cursor : null
  const dateValid = (value?: string | null) =>
    !value ||
    (/^\d{4}-\d{2}-\d{2}$/.test(value) &&
      value >= '0001-01-01' &&
      value <= '9999-12-30' &&
      !Number.isNaN(Date.parse(value)) &&
      new Date(value + 'T00:00:00Z').toISOString().slice(0, 10) === value)
  const shellLabels = { admin: a.admin, seller: a.seller, supply: a.supply }
  const groups = [
    ['status', statuses],
    ['shell', ['admin', 'seller', 'supply']],
    ['kind', ['bug', 'improvement']],
    ['impact', ['blocking', 'difficult', 'minor']],
  ] as const
  return (
    <div className="space-y-4 text-sm">
      <form
        onSubmit={(e) => {
          e.preventDefault()
          if (
            !dateValid(draft.submitted_from) ||
            !dateValid(draft.submitted_to) ||
            (draft.submitted_from &&
              draft.submitted_to &&
              draft.submitted_from > draft.submitted_to)
          ) {
            setInvalid(true)
            return
          }
          setInvalid(false)
          setCursor(undefined)
          setFilters(draft)
        }}
        className="space-y-3"
      >
        <details>
          <summary className="cursor-pointer font-medium">{a.filters}</summary>
          <div className="mt-3 grid grid-cols-1 gap-3 min-[420px]:grid-cols-2">
            {groups.map(([key, values]) => (
              <label key={key} className="space-y-1">
                {a[key]}
                <select
                  aria-label={a[key]}
                  className={reportInputClass}
                  value={draft[key] ?? ''}
                  onChange={(e) =>
                    setDraft({ ...draft, [key]: e.target.value || undefined })
                  }
                >
                  <option value="">{a.all}</option>
                  {values.map((value) => (
                    <option key={value} value={value}>
                      {key === 'shell'
                        ? shellLabels[value as keyof typeof shellLabels]
                        : t[value as keyof typeof t]}
                    </option>
                  ))}
                </select>
              </label>
            ))}
            <DateRangePicker
              label={`${a.from} – ${a.to}`}
              value={{
                start: parsePlainDate(draft.submitted_from ?? ''),
                end: parsePlainDate(draft.submitted_to ?? ''),
              }}
              onChange={(range) =>
                setDraft({
                  ...draft,
                  submitted_from: serializePlainDate(range.start) || undefined,
                  submitted_to: serializePlainDate(range.end) || undefined,
                })
              }
            />
          </div>
          <Button className="mt-3" variant="outline" type="submit">
            {a.apply}
          </Button>
        </details>
        {invalid && <p role="alert">{a.dates}</p>}
      </form>
      {query.isPending ? (
        <p role="status">{t.loading}</p>
      ) : query.isError || query.data?.status !== 200 ? (
        <div role="alert">
          {t.error}
          <Button onClick={() => void query.refetch()}>{t.retry}</Button>
        </div>
      ) : (
        <>
          {!query.data.data.items.length && (
            <p className="py-8 text-center text-[var(--color-text-muted)]">
              {a.empty}
            </p>
          )}
          {query.data.data.items.map((report) => (
            <button
              key={report.id}
              onClick={() => onSelect(report.id)}
              className="block w-full space-y-2 rounded-[var(--radius-control)] border border-[var(--color-border)] p-3 text-left focus-visible:outline-2 focus-visible:outline-[var(--color-focus)]"
            >
              <span className="flex justify-between gap-2 font-medium">
                <span>{report.reference}</span>
                <span>{t[report.status]}</span>
              </span>
              <span className="block line-clamp-2 break-words">
                {report.description}
              </span>
              <span className="block text-xs text-[var(--color-text-muted)]">
                {report.impact ? t[report.impact] : t.notSpecified} ·{' '}
                {report.route_name}
              </span>
              <time
                dateTime={report.created_at}
                className="block text-xs text-[var(--color-text-muted)]"
              >
                {a.age}: {formatDateTime(report.created_at)}
              </time>
            </button>
          ))}
          <div className="flex justify-between gap-2">
            {cursor && (
              <Button variant="ghost" onClick={() => setCursor(undefined)}>
                {t.back}
              </Button>
            )}
            {query.data.data.has_more && query.data.data.next_cursor && (
              <Button
                variant="outline"
                onClick={() => setCursor(nextCursor ?? undefined)}
              >
                {t.next}
              </Button>
            )}
          </div>
        </>
      )}
    </div>
  )
}
